/**
 * Multi-Selection Service
 *
 * Owns the editor's {@link SelectionModel} (the multi-object selection set) and
 * wires it to the sandbox:
 *
 * - Listens to `trace-viewer:selection-changed` and routes each click into the
 *   model as either additive (ctrl/shift) or replace (plain click). This covers
 *   viewport clicks *and* Inspector-tree clicks (the latter are always
 *   non-additive → replace, which is correct — the stock Inspector tree has no
 *   multi-select).
 * - Keeps the viewport outline in sync with the full set via
 *   `viewer.setOutlinedSelectionSet`.
 *
 * Re-entrancy: the model mirrors its primary into `TraceScene.select()`, which
 * re-fires `selection-changed`. A guard flag suppresses the listener while the
 * model is the one driving the scene, so we never loop.
 */

import type { TraceViewerElement, SceneObject } from 'trace-viewer';
import { SelectionModel, type SelectionChange } from 'trace-viewer/editor';
import { suppressSelectionBridge } from './selection-bridge-service';

export class MultiSelectionService {
    private readonly viewer: TraceViewerElement;
    private readonly model: SelectionModel;
    private mirroring = false;
    private modifierHeld = false;
    private pointerDown = false;
    private readonly _onSelectionChanged: (e: Event) => void;
    private readonly _onKey: (e: KeyboardEvent) => void;
    private readonly _onBlur: () => void;
    private readonly _onPointerDown: () => void;
    private readonly _onPointerUp: () => void;
    private readonly _unsubModel: () => void;

    /**
     * Called whenever the settled primary changes. This is the authoritative
     * signal for "what is the selected object now" — prefer it over the raw
     * `selection-changed` event, which can fire transiently mid-reconcile.
     */
    onPrimaryChanged: ((primary: SceneObject | null) => void) | null = null;

    constructor(viewer: TraceViewerElement) {
        this.viewer = viewer;
        if (!viewer.traceScene) {
            throw new Error('[MultiSelectionService] viewer.traceScene not ready');
        }
        this.model = new SelectionModel(viewer.traceScene);

        // Track modifier keys ourselves. Two reasons:
        // 1. The Inspector picks on pointer-down and drives selection through the
        //    selection-bridge, which calls scene.select() WITHOUT the additive
        //    flag — so we can't rely solely on the event's `additive`.
        // 2. While a modifier is held we suppress the bridge's inspector→viewport
        //    select so only ClickToSelect's additive event reaches the model
        //    (otherwise the bridge's flagless select collapses the set first).
        this._onKey = (e: KeyboardEvent) => {
            const held = e.ctrlKey || e.metaKey || e.shiftKey;
            if (held === this.modifierHeld) return;
            this.modifierHeld = held;
            suppressSelectionBridge(held);
        };
        this._onBlur = () => {
            if (!this.modifierHeld) return;
            this.modifierHeld = false;
            suppressSelectionBridge(false);
        };
        // Track whether a pointer is actually down. A modifier-held select only
        // means "additive" during a real click — NOT for a programmatic
        // traceScene.select() that happens while a modifier shortcut key is held
        // (e.g. Cmd+D duplicate, which would otherwise add the new object to the
        // set and spuriously form a group).
        this._onPointerDown = () => { this.pointerDown = true; };
        this._onPointerUp = () => { this.pointerDown = false; };
        window.addEventListener('keydown', this._onKey);
        window.addEventListener('keyup', this._onKey);
        window.addEventListener('blur', this._onBlur);
        window.addEventListener('pointerdown', this._onPointerDown, true);
        window.addEventListener('pointerup', this._onPointerUp, true);

        // Keep the outline in sync with the set, and surface the settled primary.
        // While a single object is selected we release outline control (null) so
        // the default single-primary path applies; once >1 are selected we drive
        // the set.
        this._unsubModel = this.model.onChanged((change: SelectionChange) => {
            if (change.set.size > 1) {
                this.viewer.setOutlinedSelectionSet(change.set);
            } else {
                this.viewer.setOutlinedSelectionSet(null);
            }
            this.onPrimaryChanged?.(change.primary);
        });

        this._onSelectionChanged = (e: Event) => {
            // Ignore the echo from our own mirror to the scene.
            if (this.mirroring) return;

            const detail = (e as CustomEvent<{ object?: SceneObject | null; additive?: boolean }>).detail;
            const obj = detail?.object ?? null;
            // Additive if the event says so, OR a modifier is held DURING a real
            // pointer interaction (covers the Inspector's flagless pick-on-
            // pointerdown). The pointerDown gate prevents programmatic selects
            // during a held shortcut key (Cmd+D) from being treated as additive.
            const additive = (detail?.additive ?? false) || (this.modifierHeld && this.pointerDown);

            this.mirroring = true;
            // Suppress the inspector→viewport bridge while we mirror to the scene,
            // so the Inspector's selectedEntity observer can't re-drive
            // scene.select() and collapse the set. Restore to the modifier state
            // (which may already be suppressing the bridge) afterward.
            suppressSelectionBridge(true);
            try {
                if (!obj) {
                    // Clicking empty space deselects. (This arrives via
                    // handlePointerMiss → select(null), which carries no additive
                    // flag, so it always clears — including an empty ctrl-click.)
                    if (!additive) this.model.clear();
                } else if (additive) {
                    this.model.toggle(obj);
                } else {
                    this.model.replace(obj);
                }
            } finally {
                suppressSelectionBridge(this.modifierHeld);
                this.mirroring = false;
            }
        };
        window.addEventListener('trace-viewer:selection-changed', this._onSelectionChanged);
    }

    /** The underlying selection model (primary + set). */
    getModel(): SelectionModel {
        return this.model;
    }

    /** Objects in the selection set, primary last. Prunes disposed refs first. */
    getSelectedObjects(): SceneObject[] {
        // Objects can be deleted via paths that bypass our selection-changed
        // listener (AI tools, catalog/resources panels), leaving stale refs.
        // Prune before handing the set to a batch op (delete/group).
        this.model.pruneMissing();
        return this.model.list();
    }

    /** The primary (last-clicked) object, or null. */
    getPrimary(): SceneObject | null {
        return this.model.primary;
    }

    /** Drop any selected objects no longer present in the scene (post-mutation). */
    pruneMissing(): void {
        this.model.pruneMissing();
    }

    /** Clear the entire selection. */
    clear(): void {
        this.mirroring = true;
        try {
            this.model.clear();
        } finally {
            this.mirroring = false;
        }
    }

    dispose(): void {
        window.removeEventListener('trace-viewer:selection-changed', this._onSelectionChanged);
        window.removeEventListener('keydown', this._onKey);
        window.removeEventListener('keyup', this._onKey);
        window.removeEventListener('blur', this._onBlur);
        window.removeEventListener('pointerdown', this._onPointerDown, true);
        window.removeEventListener('pointerup', this._onPointerUp, true);
        suppressSelectionBridge(false);
        this._unsubModel();
        this.viewer.setOutlinedSelectionSet(null);
    }
}
